const getUserSession = async (uuid_value, current_session_id) => {
  try {
    const response = await fetch(
      `/get_session?uuid=${uuid_value}&current_session_id=${current_session_id}`
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.log("session api errors:", error);
    return { error: "Sesion Error" };
  }
};

const inferUserPreferences = async ({ railsTimezone, ianaTimezone, locale }) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation InferUserPreferences($railsTimezone: String, $ianaTimezone: String, $locale: String) {
          inferUserPreferences(railsTimezone: $railsTimezone, ianaTimezone: $ianaTimezone, locale: $locale) {
            status
            userAccountInfo
          }
        }
      `,
      variables: {
        railsTimezone,
        ianaTimezone,
        locale,
      },
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    if (json.errors && json.errors.length) {
      console.log("infer user preferences errors:", json.errors);
      return null;
    }
    return json.data?.inferUserPreferences || null;
  } catch (error) {
    console.log("infer user preferences api error:", error);
    return null;
  }
};

const getChatWorkspaceLayout = async () => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        query ChatWorkspaceLayout {
          chatWorkspaceLayout
        }
      `,
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    if (json.errors && json.errors.length) {
      console.log("chat workspace layout errors:", json.errors);
      return null;
    }
    return json.data?.chatWorkspaceLayout || null;
  } catch (error) {
    console.log("chat workspace layout api error:", error);
    return null;
  }
};

const updateChatWorkspaceLayout = async (layout) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation UpdateChatWorkspaceLayout($layout: JSON!) {
          updateChatWorkspaceLayout(layout: $layout)
        }
      `,
      variables: { layout },
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    if (json.errors && json.errors.length) {
      console.log("update chat workspace layout errors:", json.errors);
      return null;
    }
    return json.data?.updateChatWorkspaceLayout || null;
  } catch (error) {
    console.log("update chat workspace layout api error:", error);
    return null;
  }
};

const getChatWorkspaceState = async (threadUuid) => {
  if (!threadUuid) return null;
  try {
    const response = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          query ChatWorkspaceState($threadUuid: String!) {
            chatWorkspaceState(threadUuid: $threadUuid)
          }
        `,
        variables: { threadUuid },
      }),
    });
    const json = await response.json();
    if (json.errors?.length) return null;
    return json.data?.chatWorkspaceState || null;
  } catch (_error) {
    return null;
  }
};

const getChatWorkspaceProtocol = async () => {
  try {
    const response = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          query ChatWorkspaceProtocol {
            chatWorkspaceProtocol
          }
        `,
      }),
    });
    const json = await response.json();
    if (json.errors?.length) return null;
    return json.data?.chatWorkspaceProtocol || null;
  } catch (_error) {
    return null;
  }
};

const applyChatWorkspaceActions = async ({
  threadUuid,
  expectedRevision = undefined,
  clientActionId,
  actions,
} = {}) => {
  if (!threadUuid || !clientActionId || !Array.isArray(actions) || !actions.length) {
    return null;
  }
  try {
    const response = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `
          mutation ApplyChatWorkspaceActions(
            $threadUuid: String!
            $expectedRevision: Int
            $clientActionId: String!
            $actions: JSON!
          ) {
            applyChatWorkspaceActions(
              threadUuid: $threadUuid
              expectedRevision: $expectedRevision
              clientActionId: $clientActionId
              actions: $actions
            )
          }
        `,
        variables: {
          threadUuid,
          expectedRevision,
          clientActionId,
          actions,
        },
      }),
    });
    const json = await response.json();
    if (json.errors?.length) return { error: json.errors[0]?.message };
    return json.data?.applyChatWorkspaceActions || null;
  } catch (_error) {
    return { error: "Workspace action failed" };
  }
};

const MARKET_DECISION_FIELDS = `
  fragment MarketDecisionFields on MarketDecision {
    id
    chatThreadUuid
    title
    state
    intent
    horizonLabel
    horizonStartsAt
    horizonEndsAt
    positionState
    instrumentType
    instrumentMetadata
    thesis
    fieldProvenance
    proposalStatus
    proposalMetadata
    confirmed
    confirmedAt
    monitorStatus
    lockVersion
    readinessErrors
    assets {
      id
      allDataTickerId
      ticker
      name
      assetType
      role
      source
      confirmedAt
    }
    conditions {
      id
      role
      kind
      description
      definition
      status
      source
      position
      confirmedAt
      lastEvaluatedAt
    }
    events {
      id
      eventType
      actor
      fromState
      toState
      reason
      metadata
      occurredAt
    }
    createdAt
    updatedAt
  }
`;

const requestMarketDecisionGraphql = async (query, variables = {}) => {
  const response = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(
      json.errors.map((error) => error.message).filter(Boolean).join(". "),
    );
  }
  return json.data;
};

const getMarketDecisionWorkspace = async () => {
  const data = await requestMarketDecisionGraphql(`
    query MarketDecisionWorkspace {
      marketDecisionWorkspace {
        enabled
        decisions {
          ...MarketDecisionFields
        }
      }
    }
    ${MARKET_DECISION_FIELDS}
  `);
  return data?.marketDecisionWorkspace || { enabled: false, decisions: [] };
};

const createMarketDecisionDraft = async ({
  chatThreadUuid,
  suggestedTicker,
}) => {
  const data = await requestMarketDecisionGraphql(
    `
      mutation CreateMarketDecisionDraft(
        $chatThreadUuid: String!
        $suggestedTicker: String
      ) {
        createMarketDecisionDraft(
          chatThreadUuid: $chatThreadUuid
          suggestedTicker: $suggestedTicker
        ) {
          ...MarketDecisionFields
        }
      }
      ${MARKET_DECISION_FIELDS}
    `,
    { chatThreadUuid, suggestedTicker: suggestedTicker || null },
  );
  return data?.createMarketDecisionDraft || null;
};

const updateMarketDecisionDraft = async ({
  id,
  expectedLockVersion,
  input,
}) => {
  const data = await requestMarketDecisionGraphql(
    `
      mutation UpdateMarketDecisionDraft(
        $id: ID!
        $expectedLockVersion: Int!
        $input: MarketDecisionDraftInput!
      ) {
        updateMarketDecisionDraft(
          id: $id
          expectedLockVersion: $expectedLockVersion
          input: $input
        ) {
          ...MarketDecisionFields
        }
      }
      ${MARKET_DECISION_FIELDS}
    `,
    { id, expectedLockVersion, input },
  );
  return data?.updateMarketDecisionDraft || null;
};

const confirmMarketDecision = async ({ id, expectedLockVersion }) => {
  const data = await requestMarketDecisionGraphql(
    `
      mutation ConfirmMarketDecision($id: ID!, $expectedLockVersion: Int!) {
        confirmMarketDecision(
          id: $id
          expectedLockVersion: $expectedLockVersion
        ) {
          ...MarketDecisionFields
        }
      }
      ${MARKET_DECISION_FIELDS}
    `,
    { id, expectedLockVersion },
  );
  return data?.confirmMarketDecision || null;
};

const userShared = (shareAnswer, shareHash, hasShared) => {
  let requestOptions;
  if (!hasShared) {
    requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `mutation userSharedIncite($shareAnswer: Boolean!, $shareHash: String!) {
          userSharedIncite(shareAnswer: $shareAnswer, shareHash: $shareHash) {
            status
          }
        }`,
        variables: {
          shareAnswer: shareAnswer,
          shareHash: shareHash,
        },
      }),
    };
  } else {
    // Now we set that they have shared
    requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `mutation setUserHasShared($hasShared: Boolean!) {
          setUserHasShared(hasShared: $hasShared) {
            status
          }
        }`,
        variables: {
          hasShared: hasShared,
        },
      }),
    };
  }
  let final = fetch("/graphql", requestOptions)
    .then((response) => response.json())
    .then((data) => {
      if (data.errors && data.errors.length > 0) {
        console.log("share error?", data.errors);
        return "ERROR";
      } else {
        return data.data;
      }
    })
    .catch((error) => {
      console.log("api errors:", error);
      return "ERROR";
    });
  return final;
};

const userRatedMessage = (vote) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation userRatedMessage($vote: String!) {
        userRatedMessage(vote: $vote) {
          status
        }
      }`,
      variables: {
        vote: vote,
      },
    }),
  };
  let final = fetch("/graphql", requestOptions)
    .then((response) => response.json())
    .then((data) => {
      if (data.errors && data.errors.length > 0) {
        console.log("user rating error?", data.errors);
        return "ERROR";
      } else {
        return data.data;
      }
    })
    .catch((error) => {
      console.log("api errors:", error);
      return "ERROR";
    });
  return final;
};

const userShareExperienceButton = (buttonSelected) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation userSetShareExperience($buttonSelected: String!) {
        userSetShareExperience(buttonSelected: $buttonSelected) {
          status
        }
      }`,
      variables: {
        buttonSelected: buttonSelected,
      },
    }),
  };
  let final = fetch("/graphql", requestOptions)
    .then((response) => response.json())
    .then((data) => {
      if (data.errors && data.errors.length > 0) {
        console.log("user rating error?", data.errors);
        return "ERROR";
      } else {
        return data.data;
      }
    })
    .catch((error) => {
      console.log("api errors:", error);
      return "ERROR";
    });
  return final;
};

const getWatchlistData = (setWatchlistData) => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({query: `
      {
        userWatchlistData {
          id
          assetType
          name
          ticker
          exchangeCountry
          action
          change
          updatedAt
          currentPrice
          pricePctChange1d
          pricePctChange24hr
        }
      }`
    })
  };
  fetch('/graphql', requestOptions)
  .then(response => response.json())
  .then(data => {
    const responseData = data.data.userWatchlistData
    setWatchlistData({
      Stocks: responseData.filter((v) => v.assetType == "stocks").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Cryptos: responseData.filter((v) => v.assetType == "cryptos").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      ForexPairs: responseData.filter((v) => v.assetType == "forex").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Indices: responseData.filter((v) => v.assetType == "indices").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Commodities: responseData.filter((v) => v.assetType == "commodities").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    })
  })
  .catch(error => console.error('Error fetching data:', error));
};

const updateUserWatchlist = (tickerIdsAdd, tickerIdRemove, setWatchlistData, setIsRefreshing) => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation updateUserWatchlistData($tickerIdsAdd: [ID!], $tickerIdRemove: ID) {
        updateUserWatchlistData(tickerIdsAdd: $tickerIdsAdd, tickerIdRemove: $tickerIdRemove) {
          id
          assetType
          name
          ticker
          exchangeCountry
          action
          change
          updatedAt
          currentPrice
          pricePctChange1d
          pricePctChange24hr
        }
      }`,
      variables: {
        tickerIdsAdd: tickerIdsAdd,
        tickerIdRemove: tickerIdRemove,
      },
    })
  };
  return fetch('/graphql', requestOptions)
  .then(response => response.json())
  .then(data => {
    if (data.errors && data.errors.length) {
      console.error('GraphQL errors:', data.errors);
      return { error: data.errors[0]?.message || "Failed to update watchlist" };
    }
    const responseData = data?.data?.updateUserWatchlistData
    if (!Array.isArray(responseData)) {
      return { error: "Invalid watchlist response" };
    }
    setWatchlistData({
      Stocks: responseData.filter((v) => v.assetType == "stocks").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Cryptos: responseData.filter((v) => v.assetType == "cryptos").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      ForexPairs: responseData.filter((v) => v.assetType == "forex").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Indices: responseData.filter((v) => v.assetType == "indices").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      Commodities: responseData.filter((v) => v.assetType == "commodities").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    })
    if (tickerIdsAdd && tickerIdsAdd.length > 0) {
      updateWatchlistData(tickerIdsAdd, setWatchlistData, true, setIsRefreshing)
    }
    return { ok: true };
  })
  .catch(error => {
    console.error('Error fetching data:', error);
    return { error: "Failed to update watchlist" };
  });
};

const updateWatchlistData = (tickerIdsUpdate, setWatchlistData, forceRefresh, setIsRefreshing) => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation updateWatchlistData($tickerIdsUpdate: [ID!], $forceRefresh: Boolean!) {
        updateWatchlistData(tickerIdsUpdate: $tickerIdsUpdate, forceRefresh: $forceRefresh) {
          id
          assetType
          name
          ticker
          exchangeCountry
          action
          change
          updatedAt
          currentPrice
          pricePctChange1d
          pricePctChange24hr
        }
      }`,
      variables: {
        tickerIdsUpdate: tickerIdsUpdate,
        forceRefresh: forceRefresh,
      },
    })
  };
  return fetch('/graphql', requestOptions)
  .then(response => response.json())
  .then(data => {
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error('Error in GraphQL response');
    }
    const responseData = data.data.updateWatchlistData

    setWatchlistData(prevData => {
      const map = new Map();
      // Fill map with the existing data
      ['Stocks', 'Cryptos', 'ForexPairs', 'Indices', 'Commodities'].forEach(type => {
        prevData[type]?.forEach(item => map.set(item.id, item));
      });
      // Update with new data, overriding where necessary
      responseData.forEach(item => {
        map.set(item.id, { ...map.get(item.id), ...item });
      });
      const updatedData = {
        Stocks: Array.from(map.values()).filter(item => item.assetType === "stocks").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        Cryptos: Array.from(map.values()).filter(item => item.assetType === "cryptos").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        ForexPairs: Array.from(map.values()).filter(item => item.assetType === "forex").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        Indices: Array.from(map.values()).filter(item => item.assetType === "indices").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        Commodities: Array.from(map.values()).filter(item => item.assetType === "commodities").sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      };
      return updatedData;
    });
    setIsRefreshing(false)
    return "success";
  })
  .catch(error => {
    console.error('Error fetching data:', error);
    setIsRefreshing(false)
    return "error";
  });
};

const refreshWatchlist = async (
  {
    lastRequestedUpdateWatchlist,
    setLastRequestedUpdateWatchlist,
    setIsRefreshing,
    setWatchlistData,
    setHasErroredWatchlist,
  },
  tickerIdsNullAction,
  tickerIdsOutdatedAction,
  forceRefresh
) => {
  const currentTime = new Date().getTime();
  let tickerIdsUpdate = null;
  if (tickerIdsNullAction != null && tickerIdsOutdatedAction != null) {
    tickerIdsUpdate = tickerIdsNullAction.concat(tickerIdsOutdatedAction);
  }
  if (lastRequestedUpdateWatchlist !== null) {
    const diff = currentTime - lastRequestedUpdateWatchlist;
    if (diff < 1000 * 30) {
      // If not at leat 30 seconds, don't request again.
      return;
    }
  }
  setLastRequestedUpdateWatchlist(currentTime);
  setIsRefreshing(true);
  try {
    const result = await updateWatchlistData(
      tickerIdsUpdate,
      setWatchlistData,
      forceRefresh,
      setIsRefreshing
    );
    console.log("refreshResult", result);
    setHasErroredWatchlist(result === "error");
  } catch (error) {
    console.error("Error during refresh:", error);
    setHasErroredWatchlist(true);
  } finally {
    console.log("We set refresh back to false.");
    setIsRefreshing(false);
  }
};

const clearUserMemory = async () => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `
        mutation {
          clearUserMemory {
            status
          }
        }
      `
    })
  };
  const response = await fetch('/graphql', requestOptions);
  const json = await response.json();
  return json.data?.clearUserMemory;
};

const getUserTradeProposals = async (limit = null) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        query UserTradeProposals($limit: Int) {
          userTradeProposals(limit: $limit) {
            id
            chatMessageId
            allDataTickerId
            ticker
            name
            assetType
            entryPrice
            stopLoss
            priceTarget
            expectedTradeDuration
            notes
            status
            exitReason
            latestEventKind
            latestEventAt
            currentPrice
            currentPriceUpdatedAt
            livePriceAtSuggested
            livePriceAtSuggestedUpdatedAt
            percentChangeFromEntry
            percentToTarget
            percentToStopLoss
            percentTimePassed
            rewardRiskRatio
            tracked
            createdAt
            updatedAt
          }
        }
      `,
      variables: { limit },
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    const data = json.data?.userTradeProposals || [];
    if (json.errors && json.errors.length) {
      console.error("userTradeProposals errors:", json.errors);
    }
    return data;
  } catch (error) {
    console.error("getUserTradeProposals error:", error);
    return { error: "Failed to load trade proposals" };
  }
};

const updateTradeProposalTracking = async (chatMessageId, tracked) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation UpdateTradeProposalTracking($chatMessageId: ID!, $tracked: Boolean!) {
          updateTradeProposalTracking(chatMessageId: $chatMessageId, tracked: $tracked) {
            id
            tracked
          }
        }
      `,
      variables: { chatMessageId, tracked },
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    if (json.errors && json.errors.length) {
      console.error("updateTradeProposalTracking errors:", json.errors);
      return { error: json.errors[0].message || "Failed to update tracking" };
    }
    return json.data?.updateTradeProposalTracking || null;
  } catch (error) {
    console.error("updateTradeProposalTracking error:", error);
    return { error: "Failed to update tracking" };
  }
};

const clearUserTradeProposals = async (scope) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation ClearUserTradeProposals($scope: String!) {
          clearUserTradeProposals(scope: $scope) {
            status
          }
        }
      `,
      variables: { scope },
    }),
  };

  try {
    const response = await fetch("/graphql", requestOptions);
    const json = await response.json();
    if (json.errors && json.errors.length) {
      console.error("clearUserTradeProposals errors:", json.errors);
      return { error: json.errors[0].message || "Failed to clear trades" };
    }
    return json.data?.clearUserTradeProposals || null;
  } catch (error) {
    console.error("clearUserTradeProposals error:", error);
    return { error: "Failed to clear trades" };
  }
};

export {
  getUserSession,
  inferUserPreferences,
  getChatWorkspaceLayout,
  updateChatWorkspaceLayout,
  getChatWorkspaceState,
  getChatWorkspaceProtocol,
  applyChatWorkspaceActions,
  getMarketDecisionWorkspace,
  createMarketDecisionDraft,
  updateMarketDecisionDraft,
  confirmMarketDecision,
  userShared,
  userRatedMessage,
  userShareExperienceButton,
  getWatchlistData,
  updateUserWatchlist,
  updateWatchlistData,
  refreshWatchlist,
  clearUserMemory,
  getUserTradeProposals,
  updateTradeProposalTracking,
  clearUserTradeProposals,
};
